/**
 * How many working days the worker calendar shows at once.
 *
 * `day` is the one-column view: the full width goes to a single day, so a block can carry the
 * address, the tenant and the phone number that a ~85px column in `three` has to drop.
 */
export const CALENDAR_VIEWS = ['day', 'three'] as const
export type CalendarView = (typeof CALENDAR_VIEWS)[number]

export const DEFAULT_CALENDAR_VIEW: CalendarView = 'three'

export const dayCountFor = (view: CalendarView): number => (view === 'day' ? 1 : 3)

/**
 * The view this browser last chose, kept so a worker who prefers one day gets it on the next app
 * open without having to touch the toggle again. The URL (`calView`) still wins when it is present.
 *
 * Every access is wrapped: Safari in private mode throws on localStorage, and a calendar that
 * cannot remember its view is still a calendar — same treatment as `shared/lib/push`.
 */
const STORAGE_KEY = 'worker-calendar-view'

const isCalendarView = (value: unknown): value is CalendarView =>
  (CALENDAR_VIEWS as readonly unknown[]).includes(value)

export function readStoredCalendarView(): CalendarView | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isCalendarView(raw) ? raw : null
  } catch {
    return null
  }
}

export function writeStoredCalendarView(view: CalendarView): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, view)
  } catch {
    // Nothing to do — the URL still carries the choice for this session.
  }
}
